import React, { useEffect, lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import StockDetail from './pages/StockDetail';
import StockDetailNew from './pages/StockDetailNew';
import AIEcosystemTrade from './pages/AIEcosystemTrade';
import AIEcosystemAnalysis from './pages/AIEcosystemAnalysis';
import MetalEcosystemAnalysis from './pages/MetalEcosystemAnalysis';
import OilEcosystemAnalysis from './pages/OilEcosystemAnalysis';
import MetalEcosystemAnalysisEnhanced from './pages/MetalEcosystemAnalysisEnhanced';
import OilEcosystemAnalysisEnhanced from './pages/OilEcosystemAnalysisEnhanced';
import HealthcareEcosystemAnalysisEnhanced from './pages/HealthcareEcosystemAnalysisEnhanced';
import FinanceEcosystemAnalysisEnhanced from './pages/FinanceEcosystemAnalysisEnhanced';
import TechEcosystemAnalysisEnhanced from './pages/TechEcosystemAnalysisEnhanced';
import RealEstateEcosystemAnalysisEnhanced from './pages/RealEstateEcosystemAnalysisEnhanced';
import './styles/StockDetailNew.css';
import StockList from './components/StockList';
import TradingTerminal from './components/TradingTerminal';
import MarketSelector from './components/MarketSelector';
import SignalsBoard from './components/SignalsBoard';
const ChatBox = lazy(() => import('./components/ChatBox'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
import { useI18n } from './i18n';
import US_FLAG from './assets/flags/us.svg';
import VN_FLAG from './assets/flags/vn.svg';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  Tooltip,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, BarController, Tooltip);

// Error Boundary wrapper to catch React errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Log error but don't show it for checkout-related errors from extensions
    if (!error?.message?.includes('checkout')) {
      console.error('App Error:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-primary)' }}>
          <p>Something went wrong. Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App(){
  const { t, lang, setLang, theme, setTheme } = useI18n();
  const [market, setMarket] = useState('US');

  useEffect(()=>{
    try{
      document.body.classList.remove('light-theme','dark-theme');
      document.body.classList.add(theme === 'dark' ? 'dark-theme' : 'light-theme');
    }catch(e){}
  }, [theme]);

  function Home(){
    const todaysDate = new Date();
    return (
      <div className="app pro-shell">
        <nav className="navbar pro-nav">
          <div className="nav-left">
            <div className="logo-block">
              <div className="logo">{t('app.title')}</div>
              <span className="terminal-tag">AI Trade Ops</span>
            </div>
            <div className="session-pills">
              <span className="session-pill live">LIVE</span>
              <span className="session-pill secondary">{todaysDate.toLocaleDateString()}</span>
            </div>
          </div>
          <div className="nav-right">
            <MarketSelector market={market} onMarketChange={setMarket} compact={true} />
            <div className="nav-kpi">
              <span>API</span>
              <strong>Ready</strong>
            </div>
            <button
              className="lang-btn"
              onClick={() => setLang(lang === 'vi' ? 'en' : 'vi')}
              aria-label={lang === 'vi' ? 'Switch to English' : 'Switch to Vietnamese'}
              title={lang === 'vi' ? 'Switch to English' : 'Chuyển sang tiếng Việt'}
            >
              <img src={lang === 'vi' ? US_FLAG : VN_FLAG} alt={lang === 'vi' ? 'English' : 'Vietnamese'} />
              <span className="sr-only">{lang === 'vi' ? 'Switch to English' : 'Chuyển sang tiếng Việt'}</span>
            </button>
            <button className="theme-btn" onClick={()=>setTheme(theme==='dark'?'light':'dark')} aria-label={theme==='dark'?'Switch to light':'Switch to dark'} title={theme==='dark'?'Switch to light':'Switch to dark'}>{theme==='dark' ? '☀️' : '🌙'}</button>
          </div>
        </nav>

        <section className="terminal-stage fade-in">
          <div className="stage-intro">
            <h1>{t('hero.headline')}</h1>
            <p>{t('hero.subtitle')}</p>
            <div className="intro-actions">
              <button onClick={() => window.location.href = '/signals'}>🎯 View Swing Signals</button>
              <button onClick={() => window.location.href = '/ai-ecosystem'}>🤖 AI Ecosystem</button>
              <button onClick={() => window.location.href = '/metals-ecosystem-enhanced'}>⛏️ Metals</button>
              <button onClick={() => window.location.href = '/oil-ecosystem-enhanced'}>🛢️ Oil & Energy</button>
              <button onClick={() => window.location.href = '/healthcare-ecosystem'}>🏥 Healthcare</button>
              <button onClick={() => window.location.href = '/finance-ecosystem'}>💰 Finance</button>
              <button onClick={() => window.location.href = '/tech-ecosystem'}>💻 Technology</button>
              <button onClick={() => window.location.href = '/realestate-ecosystem'}>🏢 Real Estate</button>
              <button className="ghost">Share Session</button>
            </div>
          </div>
          <div className="stage-kpis">
            {[{
              label: 'Signals today',
              value: '128',
              detail: '+12% QoQ'
            },{
              label: 'Capital deployed',
              value: '$42.3M',
              detail: 'multi-market'
            },{
              label: 'AI coverage',
              value: '312 symbols',
              detail: 'US · CA · VN'
            }].map((card) => (
              <div key={card.label} className="stage-card">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.detail}</small>
              </div>
            ))}
          </div>
        </section>

        <TradingTerminal market={market} onMarketChange={setMarket} />

        <section className="panel scanner-panel fade-in" aria-label="Market scanner">
          <div className="panel-head">
            <div>
              <p className="eyebrow">Deep Watch</p>
              <h3>{t('features.title')}</h3>
            </div>
            <p className="panel-subtitle">{t('features.subtitle')}</p>
          </div>
          <StockList market={market} onMarketChange={setMarket} />
        </section>

        <Suspense fallback={<div style={{color:'var(--text-secondary)'}}>Loading chat…</div>}>
          <ChatBox apiBase={import.meta.env.VITE_API_URL || ''} initialOpen={false} />
        </Suspense>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/signals" element={<SignalsBoard />} />
          <Route path="/ai-ecosystem" element={<AIEcosystemTrade />} />
          <Route path="/ai-ecosystem-analysis" element={<AIEcosystemAnalysis />} />
          <Route path="/metals-ecosystem" element={<MetalEcosystemAnalysis />} />
          <Route path="/oil-ecosystem" element={<OilEcosystemAnalysis />} />
          <Route path="/metals-ecosystem-enhanced" element={<MetalEcosystemAnalysisEnhanced />} />
          <Route path="/oil-ecosystem-enhanced" element={<OilEcosystemAnalysisEnhanced />} />
          <Route path="/healthcare-ecosystem" element={<HealthcareEcosystemAnalysisEnhanced />} />
          <Route path="/finance-ecosystem" element={<FinanceEcosystemAnalysisEnhanced />} />
          <Route path="/tech-ecosystem" element={<TechEcosystemAnalysisEnhanced />} />
          <Route path="/realestate-ecosystem" element={<RealEstateEcosystemAnalysisEnhanced />} />
          <Route path="/stock/:symbol" element={<StockDetailNew />} />
          <Route path="/stock-old/:symbol" element={<StockDetail />} />
          <Route
            path="/admin"
            element={(
              <Suspense fallback={<div style={{ color: 'var(--text-secondary)', padding: '20px' }}>Loading admin…</div>}>
                <AdminDashboard />
              </Suspense>
            )}
          />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

